import { computed } from 'vue';
import {
  codeProfileRouteBasedCalConfig,
  timeLabels,
  toolbarTimeOptions,
} from '../components/Main/main.config';
import timeFormat from '../utils/time/timeFormat';
import {
  useActions,
  useDispatch,
  useGetters,
  useState,
} from '../utils/useMappers';
import { useRoute } from 'vue-router';

export function useTime() {
  const [
    customRange,
    isCompareCustom,
    durationShow,
    getCurrTimeLabel,
    getCompareDurationRow,
  ] = useGetters([
    'globalState/settings/getCustomRange',
    'globalState/settings/getIsCompareCustom',
    'globalState/settings/durationShow',
    'globalState/settings/getCurrTimeLabel',
    'globalState/settings/getCompareDurationRow',
  ]);

  const [app] = useState(['globalState/app']);

  const [setCustomRange] = useDispatch(['globalState/settings/setCustomRange']);
  const [setDuration, setCurrTimeLabel, setIsForcastEnabled] = useActions([
    'globalState/settings/setDuration',
    'globalState/settings/setCurrTimeLabel',
    'globalState/app/setIsForcastEnabled',
  ]);

  const [setDisablePrevious, setCompareDuration, setIsCompareOptionsSelected] =
    useDispatch([
      'globalState/settings/setDisablePrevious',
      'globalState/settings/setCompareDuration',
      'globalState/settings/setIsCompareOptionsSelected',
    ]);

  const route = useRoute();

  const timeSelectOptions = computed(() => {
    return toolbarTimeOptions
      .filter(
        (obj) => !app.value?.disableDateTimePickerOptions?.includes(obj.name),
      )
      .filter((obj) =>
        codeProfileRouteBasedCalConfig.routes.includes(route?.name)
          ? parseInt(obj.value) <= codeProfileRouteBasedCalConfig.value
          : true,
      );
  });

  const isCustomDuration = computed(() => {
    return (
      !app.value?.disableDateTimePickerOptions?.includes(timeLabels.custom) &&
      timeLabels.custom === getCurrTimeLabel.value
    );
  });

  const selectedTime = computed(() => {
    if (
      timeSelectOptions.value.some((obj) => obj.name === getCurrTimeLabel.value)
    ) {
      return getCurrTimeLabel.value;
    }
    return isCustomDuration.value
      ? timeLabels.custom
      : timeSelectOptions.value[timeSelectOptions.value.length - 1].name;
  });

  const selectedTimeRange = computed(() => {
    return toolbarTimeOptions.find(
      (option) => option.name === selectedTime.value,
    ).value;
  });

  function setDurationFromRoute(endTimeEpoch) {
    const minDate = new Date();
    minDate.setMonth(minDate.getMonth() - 3);
    const endTime = new Date(endTimeEpoch);
    let startTime = endTime.getTime() - 60 * 60 * 1000;
    if (minDate.valueOf() < endTimeEpoch) {
      if (startTime < minDate.valueOf()) startTime = minDate.valueOf();
      const range = [new Date(startTime), endTime];
      setCustomDuration(range);
    }
  }

  function setCustomDuration(range) {
    setCustomRange(range);

    setDuration(timeFormat(range));
    setCurrTimeLabel(timeLabels.custom);
    setIsForcastEnabled(false);
    const durationGap = range[1] - range[0];
    let compareEndTime = range[0];
    let comapareStartTime = new Date(range[0] - durationGap);
    const compareRange = [comapareStartTime, compareEndTime];
    if (isCompareCustom.value) {
      setCompareCustomDuration(new Date(getCompareDurationRow.value.start));
    } else {
      setCompareDurationTime(timeFormat(compareRange));
    }
  }

  function setCompareCustomDuration(startDate) {
    setDisablePrevious(false);
    let endDate = new Date();
    const currentTime = new Date();
    if (selectedTime.value === 'Custom') {
      let customCompareGap = customRange.value[1] - customRange.value[0];
      endDate.setTime(startDate.getTime() + customCompareGap);
    } else {
      endDate.setTime(startDate.getTime() + selectedTimeRange.value);
    }
    if (endDate > currentTime) {
      endDate = currentTime;
    }
    const compareCustomRange = [startDate, endDate];
    setCompareDuration(timeFormat(compareCustomRange));
  }

  function setCompareDurationTime() {
    let compareRange = [];
    const differenceInDays =
      (durationShow.value.end - durationShow.value.start) /
      (1000 * 60 * 60 * 24);
    let compareEndTime = durationShow.value.start;
    let compareDate = new Date();
    compareDate.setDate(compareDate.getDate() - 89);
    compareDate.setHours(0, 0, 0, 0);

    if (differenceInDays >= 89) {
      setIsCompareOptionsSelected(false);
      setDisablePrevious(true);
    } else if (
      durationShow.value.start.getDate() === compareDate.getDate() &&
      durationShow.value.start.getMonth() === compareDate.getMonth()
    ) {
      setIsCompareOptionsSelected(false);
      setDisablePrevious(true);
      compareRange = [compareDate, compareEndTime];
    } else {
      setDisablePrevious(false);
    }

    let diff = durationShow.value.end - durationShow.value.start;
    let compareStartTime =
      differenceInDays > 45
        ? compareDate
        : new Date(durationShow.value.start.getTime() - diff);

    if (compareStartTime < compareDate) {
      compareStartTime = compareDate;
    }
    compareRange = [compareStartTime, compareEndTime];
    setCompareDuration(timeFormat(compareRange));
  }

  return {
    setDurationFromRoute,
    setCustomDuration,
    setCompareDurationTime,
    timeSelectOptions,
    isCustomDuration,
    selectedTime,
    selectedTimeRange,
    setCompareCustomDuration,
  };
}
